'use client'
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { useEffect, useState, useCallback, useMemo, useRef, type ReactNode } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { usePermissions } from '@/hooks/use-permissions'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { Checkbox } from '@/components/ui/checkbox'
import { MultiSelectFilter, type MultiSelectOption } from '@/components/ui/multi-select-filter'
import { Loader2, Search, Wallet, ArrowUpRight, ClipboardList, ArrowLeftRight, RotateCcw, HelpCircle, ArrowUpDown, ArrowUp, ArrowDown, ShieldAlert } from 'lucide-react'
import { toast } from 'sonner'
import { ShopifyTopupIncidentDialog } from './shopify-topup-incident-dialog'
import { TenantWalletDialog } from './tenant-wallet-dialog'
import { TenantBillingPeriodFilter } from './tenant-billing-period-filter'
import type { AdminTenantPeriod } from '@/lib/billing/admin-tenant-period'
import { getAvailableTargetChannels } from '@/lib/billing/channel-conversion/available-channels'
import {
  changePlanDialogDescription,
  defaultSyncWithStripeChecked,
  showSyncWithStripeCheckbox,
} from '@/lib/billing/admin-change-plan-with-proration/ui-predicates'
import { BILLING_CHANNELS, type BillingChannel } from '@/lib/billing/constants'

import { formatMicroUsd, formatMicroUsdSigned } from '@/lib/billing/utils/format'
import {
  computeCostMicrousd,
  computeMarginFraction,
  computeProfitMicrousd,
  computeRevenueMicrousd,
  formatMarginPercent,
  profitColorClass,
} from '@/lib/billing/admin-tenant-billing'
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"






interface AdminPlan {
  id: string
  name: string
  display_name: string
  billing_channel: string
  status: string
  price_monthly_cents: number
}

interface TenantBillingRow {
  id: string
  name: string
  is_active: boolean
  archived_at: string | null
  billing_channel: string | null
  /** Set while a Shopify top-up incident is open; survives a channel conversion. */
  shopify_topups_blocked_at: string | null
  payment_provider: string | null
  /**
   * Per-tenant override for Phase 3 self-serve plan change flag.
   * null = inherit `billing_settings.allow_self_serve_plan_change` (global),
   * true = force allow (overrides a globally-disabled flag),
   * false = force deny (overrides a globally-enabled flag).
   */
  allow_self_serve_plan_change: boolean | null
  plan_display_name: string | null
  plan_price_cents: number | null
  subscription_status: string | null
  /**
   * Stripe subscription ID, if any. Distinguishes a Stripe row with a live
   * subscription (Sync-with-Stripe checkbox default-on) from a free-flip
   * Stripe row (checkbox visible-unchecked, sync would 400). Surfaced from
   * `tenant_subscriptions.stripe_subscription_id` by the admin tenants API.
   */
  stripe_subscription_id: string | null
  balance_microusd: number
  has_active_trial: boolean
  trial_budget_microusd: number | null
  trial_spent_microusd: number | null
  /**
   * Selected-period billing aggregates from `loadAdminTenantBillingSummary`.
   * All amounts are bigint→number microUSD; sign convention follows the
   * `tenant_wallet_transactions` ledger:
   *   - subscription_revenue / topup_revenue / total_charge / total_provider_cost
   *     / manual_grant_microusd / platform_paid_cost_microusd → ≥ 0
   *   - manual_deduct_microusd → ≤ 0 (already-signed in ledger)
   *   - refund_microusd → ≥ 0 (taken absolute)
   * Cost / Profit / Margin computed client-side via {@link computeCostMicrousd} /
   * {@link computeProfitMicrousd} / {@link computeMarginFraction}.
   */
  subscription_revenue_microusd: number
  topup_revenue_microusd: number
  total_charge_microusd: number
  total_provider_cost_microusd: number
  manual_grant_microusd: number
  manual_deduct_microusd: number
  refund_microusd: number
  platform_paid_cost_microusd: number
}

type SortField =
  | 'name' | 'plan' | 'status' | 'balance'
  | 'platform_paid' | 'revenue' | 'cost' | 'profit' | 'margin'
type SortDirection = 'asc' | 'desc'

// Archived is permanent-lifecycle state; inactive without archived_at is the
// reversible Free-plan expiry state.
type TenantStatus = 'active' | 'inactive' | 'archived'

function getSTATUS_OPTIONS(tUi: ReturnType<typeof useTranslations<"Billing.BillingTenantsTab">>): MultiSelectOption[] {
  return [
  { value: 'active', label: tUi("active") },
  { value: 'inactive', label: tUi("inactive") },
  { value: 'archived', label: tUi("archived") },
]
}

const getTenantStatus = (t: TenantBillingRow): TenantStatus =>
  t.archived_at ? 'archived' : t.is_active ? 'active' : 'inactive'

/*
  Visual band for the billing columns (Platform paid / Revenue / Cost / Profit / Margin).
  Subtle amber wash + border-l/border-r at the band edges separates the
  «platform finance» group from the rest of the table without shouting. Ambar
  was picked over `bg-muted/30` because muted shaded the row as «disabled» in
  testing; amber/10 carries a financial connotation while staying whisper-quiet.
  Constants kept at module scope so future column additions (Stripe-billed
  revenue, etc.) can join the band by composition instead of duplication.
*/
const BILLING_COL = 'bg-amber-50/40 dark:bg-amber-950/20'
const BILLING_COL_FIRST = `${BILLING_COL} border-l border-amber-200/60 dark:border-amber-900/40`
const BILLING_COL_LAST = `${BILLING_COL} border-r border-amber-200/60 dark:border-amber-900/40`

/**
 * Secondary line under a Cost / Revenue cell — shows the contrast metric
 * (e.g. "spent 4000 credits" under provider Cost, "manual +5000 credits" under Revenue).
 *
 * UX rules learned in iteration:
 *  - Always render the wrapper so column heights stay aligned across rows
 *    (otherwise credit amounts look taller than `—` rows).
 *  - When `microusd === 0` we show a single em-dash instead of `0 credits` —
 *    a row of zero noise next to every Cost / Revenue value drowns the
 *    rare non-zero entries that actually matter.
 *  - `signed: true` → use `formatCreditsSigned` AND tint negatives red,
 *    because for the manual line a negative value means «admin pulled
 *    funds back from this tenant», a rare and important signal.
 */
function BillingSecondaryLine({
  label,
  microusd,
  signed = false,
}: {
  label: string
  microusd: number
  signed?: boolean
}) {
  const { formatCreditsSigned, formatCredits } = useCreditFormatters()

  const tUi = useTranslations("Billing.BillingTenantsTab")

  if (microusd === 0) {
    return (
      <div className="text-xs leading-tight text-muted-foreground/60">
        {<span key={tUi("value0", { value0: String(label) })}>{tUi("value0", { value0: String(label) })}</span>}
      </div>
    )
  }
  const value = signed ? formatCreditsSigned(microusd) : formatCredits(microusd)
  const negativeTint = signed && microusd < 0 ? 'text-destructive/80' : 'text-muted-foreground'
  return (
    <div className={`text-xs leading-tight ${negativeTint}`}>
      {<span key={tUi("value0Value1", { value0: String(label), value1: String(value) })}>{tUi("value0Value1", { value0: String(label), value1: String(value) })}</span>}
    </div>
  )
}

type SelfServeOverride = 'inherit' | 'allow' | 'deny'

function overrideToValue(v: boolean | null): SelfServeOverride {
  if (v === true) return 'allow'
  if (v === false) return 'deny'
  return 'inherit'
}

function valueToOverride(v: SelfServeOverride): boolean | null {
  if (v === 'allow') return true
  if (v === 'deny') return false
  return null
}

function subStatusBadge(status: string | null, tUi: ReturnType<typeof useTranslations<"Billing.BillingTenantsTab">>) {
  if (status === 'active') return <Badge variant="default"><span key={tUi("active")}>{tUi("active")}</span></Badge>
  if (status === 'past_due') return <Badge variant="destructive"><span key={tUi("pastDue")}>{tUi("pastDue")}</span></Badge>
  if (status === 'trialing') return <Badge variant="secondary"><span key={tUi("trial")}>{tUi("trial")}</span></Badge>
  if (status === 'canceled') return <Badge variant="outline"><span key={tUi("canceled")}>{tUi("canceled")}</span></Badge>
  return <Badge variant="outline"><span key={tUi("none")}>{tUi("none")}</span></Badge>
}

export function TenantsTab() {
  const { formatCredits } = useCreditFormatters()




  const tUi = useTranslations("Billing.BillingTenantsTab")
  const STATUS_OPTIONS = getSTATUS_OPTIONS(tUi)

  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth()
  const { can } = usePermissions()
  const [tenants, setTenants] = useState<TenantBillingRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [period, setPeriod] = useState<AdminTenantPeriod>({ period: 'this_month' })
  const requestedPeriod = useRef(period)
  const requestId = useRef(0)
  // Default to active only — archived tenants are hidden unless explicitly
  // selected, mirroring the tenant-management page.
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['active'])
  // null = no client-side sort → preserve the API's created_at-desc order
  // until the admin clicks a column header.
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Wallet dialog
  const [walletTenant, setWalletTenant] = useState<TenantBillingRow | null>(null)
  const [topupIncidentTenant, setTopupIncidentTenant] = useState<TenantBillingRow | null>(null)

  const canGrantCredits = can('billing.grant_credits')
  // The incident dialog reads through `billing.view_all`; the Tenants tab itself opens on
  // weaker permissions, so without this the button would just throw at whoever clicks it.
  const canViewAll = can('billing.view_all')
  const canManagePlans = can('billing.manage_plans')
  // Distinct from manage_plans — the plan-change override lives on tenants
  // rows, which PATCH /api/admin/billing/tenants/[id] gates on manage_tenants.
  // Showing the control to a manage_plans-only user would surface a 403 on save.
  const canManageTenants = can('billing.manage_tenants')
  // The Actions column only carries actionable controls (Wallet/grant-credits,
  // assign/change plan, channel convert/revert). A delegate who can do none of
  // them (e.g. a `billing.view_all`-only viewer) gets a clean read-only table
  // with the whole column dropped. super_user satisfies all three via wildcard.
  const hasRowActions = canManageTenants || canManagePlans || canGrantCredits

  /**
   * Per-tenant override save state keyed by tenant id. Populated while PATCH
   * is in-flight so only the touched row shows a spinner; cleared on response.
   */
  const [overrideSaving, setOverrideSaving] = useState<string | null>(null)

  /**
   * Optimistic UI for the tri-state select: updates the local row, fires the
   * PATCH, and rolls back on failure so the select reflects the DB truth.
   */
  const updateTenantOverride = useCallback(async (tenant: TenantBillingRow, next: SelfServeOverride) => {
    if (!canManageTenants) return
    const nextValue = valueToOverride(next)
    if (nextValue === tenant.allow_self_serve_plan_change) return // no-op

    const previous = tenant.allow_self_serve_plan_change
    const optimistic = (rows: TenantBillingRow[]) =>
      rows.map((r) => (r.id === tenant.id ? { ...r, allow_self_serve_plan_change: nextValue } : r))
    setTenants(optimistic)
    setOverrideSaving(tenant.id)

    try {
      const res = await fetchWithAuth(`/api/admin/billing/tenants/${tenant.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allow_self_serve_plan_change: nextValue }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { error?: string }))
        throw new Error((err as { error?: string }).error || `HTTP ${res.status}`)
      }
    } catch (err) {
      // Roll back the local state; no optimistic lie to the admin.
      const rollback = (rows: TenantBillingRow[]) =>
        rows.map((r) => (r.id === tenant.id ? { ...r, allow_self_serve_plan_change: previous } : r))
      setTenants(rollback)
      alert(err instanceof Error ? err.message : 'Failed to update plan change override')
    } finally {
      setOverrideSaving(null)
    }
  }, [canManageTenants, fetchWithAuth])

  // Assign plan dialog
  const [assignTenant, setAssignTenant] = useState<TenantBillingRow | null>(null)
  const [assignPlanId, setAssignPlanId] = useState('')
  const [assignChannel, setAssignChannel] = useState<string>('')
  const [assignLoading, setAssignLoading] = useState(false)
  // Phase 2.1 (admin-plan-change-proration): Sync-with-Stripe checkbox state.
  // Default is set by `defaultSyncWithStripeChecked` when the dialog opens;
  // null while no dialog is active.
  const [assignSyncWithStripe, setAssignSyncWithStripe] = useState(false)
  const [plans, setPlans] = useState<AdminPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [plansError, setPlansError] = useState<string | null>(null)

  // Channel conversion dialog (billing-channel-mobility Phase 3).
  const [conversionTenant, setConversionTenant] = useState<TenantBillingRow | null>(null)
  const [conversionChannel, setConversionChannel] = useState<BillingChannel | ''>('')
  const [conversionPlanId, setConversionPlanId] = useState('')
  const [conversionLoading, setConversionLoading] = useState(false)

  // Revert-to-manual confirmation.
  const [revertTenant, setRevertTenant] = useState<TenantBillingRow | null>(null)
  const [revertLoading, setRevertLoading] = useState(false)

  const loadPlans = useCallback(async () => {
    setPlansLoading(true)
    setPlansError(null)
    try {
      const res = await fetchWithAuth('/api/admin/billing/plans')
      if (!res.ok) throw new Error('Failed to load plans')
      const data = await res.json()
      setPlans((data.plans ?? []) as AdminPlan[])
    } catch (err) {
      setPlansError(err instanceof Error ? err.message : 'Failed to load plans')
    } finally {
      setPlansLoading(false)
    }
  }, [fetchWithAuth])

  const openAssignDialog = (tenant: TenantBillingRow) => {
    setAssignTenant(tenant)
    setAssignPlanId('')
    // For tenants with existing subscription: lock to current channel.
    // For legacy tenants without subscription: start empty, force explicit selection.
    setAssignChannel(tenant.subscription_status ? (tenant.billing_channel ?? '') : '')
    // Sync-with-Stripe default: on for self-serve Stripe rows with a live
    // subscription, off otherwise (manual/shopify/mock hide the checkbox
    // entirely; free-flip Stripe rows show it unchecked).
    setAssignSyncWithStripe(defaultSyncWithStripeChecked(tenant))
    loadPlans()
  }

  const handleAssignPlan = async () => {
    if (!assignTenant || !assignPlanId) return
    const isFirstAssign = !assignTenant.subscription_status
    if (isFirstAssign && !assignChannel) return
    setAssignLoading(true)
    try {
      // Three branches:
      //   1. First assignment → POST .../subscription (existing initialize flow).
      //   2. Sync-with-Stripe checked → POST .../subscription/change-plan-with-proration
      //      (admin-plan-change-proration handler — calls Stripe API, webhook
      //      then atom-syncs `plan_id`).
      //   3. Sync-unchecked → PATCH .../subscription (legacy local-only path —
      //      preserved for data reconciliation / known-manual cases).
      if (isFirstAssign) {
        const res = await fetchWithAuth(`/api/admin/billing/tenants/${assignTenant.id}/subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan_id: assignPlanId, billing_channel: assignChannel }),
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({} as { error?: string }))
          toast.error((data as { error?: string }).error || 'Failed to assign plan')
          return
        }
      } else if (assignSyncWithStripe) {
        const res = await fetchWithAuth(
          `/api/admin/billing/tenants/${assignTenant.id}/subscription/change-plan-with-proration`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ target_plan_id: assignPlanId }),
          },
        )
        if (!res.ok) {
          const data = await res.json().catch(() => ({} as Record<string, unknown>))
          const code = (data as { code?: string }).code
          // Map the 9 machine-readable validation codes to user-friendly toasts.
          const message = (() => {
            switch (code) {
              case 'target_plan_required':
                return 'Target plan is required.'
              case 'no_active_subscription':
                return 'Tenant has no active subscription — initialize billing first.'
              case 'not_self_serve':
                return 'Stripe proration sync is only available for Stripe-billed tenants. For manual / shopify / mock, uncheck "Sync with Stripe".'
              case 'no_external_subscription':
                return 'Tenant has no live Stripe subscription (free-flip state). Use "Enable self-serve" to create the initial Stripe checkout on the new plan.'
              case 'subscription_not_active': {
                const status = (data as { details?: { status?: string } }).details?.status
                return `Stripe subscription is in status '${status ?? 'unknown'}' and cannot be synced. Re-enable self-serve checkout to start a new subscription on the target plan.`
              }
              case 'interval_switch_not_supported':
                return 'Admin plan change is monthly-only. The current subscription is on a non-monthly interval.'
              case 'same_plan':
                return 'Tenant is already on the selected plan.'
              case 'same_price_switch':
                return 'Target plan has the same price as the current plan — lateral switches are not supported.'
              case 'target_plan_not_found': {
                const details = (data as { details?: Record<string, unknown> }).details ?? {}
                if ((details as { missing_stripe_price_id?: boolean }).missing_stripe_price_id) {
                  return 'Target plan has no Stripe monthly price — re-sync the plan in the Plans tab and try again.'
                }
                if ((details as { billing_channel?: string }).billing_channel) {
                  return `Target plan belongs to "${(details as { billing_channel: string }).billing_channel}" channel, not Stripe.`
                }
                return 'Target plan not found or is in draft status.'
              }
              case 'downgrade_blocked': {
                const blockers = ((data as { details?: { blockers?: string[] } }).details?.blockers) ?? []
                return blockers.length > 0
                  ? `Downgrade blocked: ${blockers.join(', ')}`
                  : 'Downgrade blocked — current usage exceeds target plan limits.'
              }
              default:
                return (data as { error?: string }).error ?? 'Failed to change plan'
            }
          })()
          toast.error(message)
          return
        }
        toast.success(tUi("planChangeSyncedToStripeWebhookWillReconcile"))
      } else {
        const res = await fetchWithAuth(`/api/admin/billing/tenants/${assignTenant.id}/subscription`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ plan_id: assignPlanId }),
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({} as { error?: string }))
          toast.error((data as { error?: string }).error || 'Failed to change plan')
          return
        }
      }
      setAssignTenant(null)
      fetchTenants()
    } catch {
      toast.error(tUi("failedToAssignPlan"))
    } finally {
      setAssignLoading(false)
    }
  }

  const handleEnableSelfServe = async (tenant: TenantBillingRow) => {
    // Tenants on a free plan flip instantly (no external billing required); paid
    // tenants need a checkout session. Confirm copy adapts so the admin knows
    // up-front which mode is about to run.
    const isFreePlan = (tenant.plan_price_cents ?? 0) === 0
    const confirmMsg = isFreePlan
      ? `Switch "${tenant.name}" to self-serve billing?\n\nTenant is on a free plan — no checkout is needed. payment_provider flips from 'manual' to '${tenant.billing_channel}' and current period dates are cleared (matches a register-flow free tenant).\n\nThe tenant will see upgrade options on their billing page only if "Allow tenants to change plan" is enabled (Settings tab) AND their per-tenant override is not "Force deny".`
      : `Create self-serve checkout for "${tenant.name}"?\n\nA checkout session will be created. The tenant must complete payment before self-serve billing activates. Current plan and balance will not change until then.`

    if (!confirm(confirmMsg)) return
    try {
      const res = await fetchWithAuth(`/api/admin/billing/tenants/${tenant.id}/subscription`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enable_self_serve: true }),
      })
      const data = await res.json()
      if (!res.ok) {
        alert(data.error || 'Failed to enable self-serve')
        return
      }
      if (data.mode === 'free_flip') {
        alert('Tenant switched to self-serve billing. Upgrades from the billing page require "Allow tenants to change plan" enabled (Settings tab) and no "Force deny" override on this tenant.')
        await fetchTenants()
      } else if (data.checkoutUrl) {
        await navigator.clipboard.writeText(data.checkoutUrl).catch(() => {})
        alert(`Checkout URL created and copied to clipboard.\n\nShare with tenant to complete payment:\n${data.checkoutUrl}`)
      }
    } catch {
      alert('Failed to enable self-serve')
    }
  }

  // ---- Channel conversion (billing-channel-mobility Phase 1 + Phase 3) ----

  const openConversionDialog = (tenant: TenantBillingRow) => {
    const targets = getAvailableTargetChannels(tenant.billing_channel as BillingChannel | null)
    // Default: pick the single available target when there's exactly one
    // (a two-channel tenant always has exactly one cross-channel target —
    // the other channel). A future ≥3-channel world surfaces a real dropdown.
    const defaultTarget = targets.length === 1 ? targets[0] : ''
    setConversionTenant(tenant)
    setConversionChannel(defaultTarget)
    setConversionPlanId('')
    loadPlans()
  }

  const handleConvertChannel = async () => {
    if (!conversionTenant || !conversionChannel || !conversionPlanId) return
    setConversionLoading(true)
    try {
      const res = await fetchWithAuth(`/api/admin/billing/tenants/${conversionTenant.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          billing_channel: conversionChannel,
          target_plan_id: conversionPlanId,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        const code = (data as { code?: string }).code
        // Map the seven machine-readable validation codes to user-facing
        // toasts. paid_cross_channel_requires_revert_first gets an explicit
        // CTA because it maps to a concrete next action (Revert button).
        const message = (() => {
          switch (code) {
            case 'paid_cross_channel_requires_revert_first':
              return 'Paid conversions require reverting to manual first. Use the Revert button in Actions, then try again.'
            case 'target_channel_not_ready':
              return `Target channel is not ready for conversions${
                (data as { details?: { reason?: string } }).details?.reason
                  ? ` (${(data as { details: { reason: string } }).details.reason})`
                  : ''
              }.`
            case 'no_active_subscription':
              return 'Tenant has no active subscription — cannot convert.'
            case 'no_change_needed':
              return 'Tenant is already on the selected channel.'
            case 'invalid_channel':
              return 'Invalid target channel.'
            case 'target_plan_required':
              return 'Target plan is required. Pick a plan from the dropdown.'
            case 'target_plan_not_found':
              return 'Target plan not found, in draft status, or on a different channel.'
            default:
              return (data as { error?: string }).error ?? 'Failed to convert channel'
          }
        })()
        toast.error(message)
        return
      }
      toast.success(tUi("channelConvertedValue0Value1", { value0: String(conversionTenant.name), value1: String(conversionChannel) }))
      setConversionTenant(null)
      await fetchTenants()
    } catch {
      toast.error(tUi("failedToConvertChannel"))
    } finally {
      setConversionLoading(false)
    }
  }

  // ---- Revert to manual (billing-channel-mobility Phase 2 + Phase 3) ----

  const openRevertDialog = (tenant: TenantBillingRow) => {
    setRevertTenant(tenant)
  }

  const handleRevertToManual = async () => {
    if (!revertTenant) return
    setRevertLoading(true)
    try {
      const res = await fetchWithAuth(
        `/api/admin/billing/tenants/${revertTenant.id}/subscription/revert-to-manual`,
        { method: 'POST' },
      )
      const data = await res.json()
      if (!res.ok) {
        const code = (data as { code?: string }).code
        const message =
          code === "already_manual"
            ? tUi("tenantIsAlreadyOnPlatformManagedManualBilling")
            : (data as { error?: string }).error ?? tUi("failedToRevertSubscription")
        toast.error(message)
        return
      }
      toast.success(tUi("revertedValue0ToManualBilling", { value0: String(revertTenant.name) }))
      setRevertTenant(null)
      await fetchTenants()
    } catch {
      toast.error(tUi("failedToRevertSubscription"))
    } finally {
      setRevertLoading(false)
    }
  }

  const fetchTenants = useCallback(async () => {
    const currentRequestId = ++requestId.current
    try {
      setLoading(true)
      setError(null)
      // A mutation that began under an earlier period must refresh the current one.
      const selection = requestedPeriod.current
      const params = new URLSearchParams({ period: selection.period })
      if (selection.period === 'custom') {
        params.set('from', selection.from)
        params.set('to', selection.to)
      }
      const query = params.size ? `?${params}` : ''
      const res = await fetchWithAuth(`/api/admin/billing/tenants${query}`)
      if (!res.ok) throw new Error('Failed to fetch tenants')
      const data = await res.json()
      if (currentRequestId !== requestId.current) return
      setTenants(data.tenants ?? [])
    } catch (err) {
      if (currentRequestId !== requestId.current) return
      setError(err instanceof Error ? err.message : tUi("failedToFetchTenants"))
    } finally {
      if (currentRequestId === requestId.current) setLoading(false)
    }
  }, [fetchWithAuth, tUi])

  useEffect(() => {
    requestedPeriod.current = period
    void fetchTenants()
    return () => { requestId.current += 1 }
  }, [fetchTenants, period])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="ml-1 h-3.5 w-3.5" />
      : <ArrowDown className="ml-1 h-3.5 w-3.5" />
  }

  const SortButton = ({ field, children }: { field: SortField; children: ReactNode }) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="inline-flex items-center hover:text-foreground transition-colors"
    >
      {children}
      <SortIcon field={field} />
    </button>
  )

  const filtered = useMemo(() => {
    let result = [...tenants]

    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter((t) => t.name.toLowerCase().includes(q))
    }

    // Empty selection = all statuses.
    if (selectedStatuses.length > 0) {
      result = result.filter((t) => selectedStatuses.includes(getTenantStatus(t)))
    }

    if (sortField) {
      result.sort((a, b) => {
        let cmp = 0
        switch (sortField) {
          case 'name':
            cmp = a.name.localeCompare(b.name)
            break
          case 'plan':
            cmp = (a.plan_display_name ?? '').localeCompare(b.plan_display_name ?? '')
            break
          case 'status':
            cmp = (a.subscription_status ?? '').localeCompare(b.subscription_status ?? '')
            break
          case 'balance':
            cmp = a.balance_microusd - b.balance_microusd
            break
          case 'platform_paid':
            cmp = a.platform_paid_cost_microusd - b.platform_paid_cost_microusd
            break
          case 'revenue':
            cmp = computeRevenueMicrousd(a) - computeRevenueMicrousd(b)
            break
          case 'cost':
            cmp = computeCostMicrousd(a) - computeCostMicrousd(b)
            break
          case 'profit':
            cmp = computeProfitMicrousd(a) - computeProfitMicrousd(b)
            break
          case 'margin':
            // Null margin (no revenue) sorts to the bottom in ascending order.
            cmp = (computeMarginFraction(a) ?? -Infinity) - (computeMarginFraction(b) ?? -Infinity)
            break
        }
        return sortDirection === 'asc' ? cmp : -cmp
      })
    }

    return result
  }, [tenants, search, selectedStatuses, sortField, sortDirection])

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-4">
      <h2 className="text-lg font-semibold"><span key={tUi("tenantBilling")}>{tUi("tenantBilling")}</span></h2>
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <TenantBillingPeriodFilter period={period} onChange={setPeriod} />
        <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center gap-2">
          <MultiSelectFilter
            labelPrefix="Status"
            pluralNoun="statuses"
            availableOptions={STATUS_OPTIONS}
            selectedValues={selectedStatuses}
            onChange={setSelectedStatuses}
            popoverWidthClassName="w-[180px]"
          />
          <div className="relative w-64 max-w-full">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={tUi("searchTenants")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center gap-2 py-12" role="status">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span><span key={tUi("loadingBillingData")}>{tUi("loadingBillingData")}</span></span>
        </div>
      )}
      {!loading && error && (
        <div className="space-y-2 py-12 text-center" role="alert">
          <p className="text-destructive">{error}</p>
          <Button variant="outline" onClick={() => void fetchTenants()}><span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button>
        </div>
      )}
      {!loading && !error && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{<span key={tUi("value0Tenants", { value0: String(filtered.length) })}>{tUi("value0Tenants", { value0: String(filtered.length) })}</span>}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead><SortButton field="name"><span key={tUi("tenant")}>{tUi("tenant")}</span></SortButton></TableHead>
                <TableHead><SortButton field="plan"><span key={tUi("plan")}>{tUi("plan")}</span></SortButton></TableHead>
                <TableHead><span key={tUi("billing")}>{tUi("billing")}</span></TableHead>
                <TableHead><SortButton field="status"><span key={tUi("status")}>{tUi("status")}</span></SortButton></TableHead>
                <TableHead className="text-right"><SortButton field="balance"><span key={tUi("balance")}>{tUi("balance")}</span></SortButton></TableHead>
                <TableHead><span key={tUi("trial")}>{tUi("trial")}</span></TableHead>
                {/*
                  Period billing-finance group: Platform paid / Revenue / Cost / Profit / Margin.
                  Subtle muted-amber wash + edge borders mark the five cells as a
                  «platform finance» band — hint that this block will likely
                  graduate to its own dedicated drill-down page later.
                  (`amber/10` keeps the financial connotation but stays whisper-quiet
                  against the default table background; muted/30 was tested first
                  and read as «disabled row» rather than a section group.)
                */}
                <TableHead className={`text-right ${BILLING_COL_FIRST}`}>
                  <span className="inline-flex items-center gap-1">
                    <SortButton field="platform_paid"><span key={tUi("platformPaid")}>{tUi("platformPaid")}</span></SortButton>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center text-muted-foreground hover:text-foreground"
                          aria-label={tUi("platformPaidTooltip")}
                        >
                          <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        <span key={tUi("providerCostOfTheAICallsThePlatform")}>{tUi("providerCostOfTheAICallsThePlatform")}</span></TooltipContent>
                    </Tooltip>
                  </span>
                </TableHead>
                <TableHead className={`text-right ${BILLING_COL}`}>
                  <span className="inline-flex items-center gap-1">
                    <SortButton field="revenue"><span key={tUi("revenue")}>{tUi("revenue")}</span></SortButton>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center text-muted-foreground hover:text-foreground"
                          aria-label={tUi("revenueTooltip")}
                        >
                          <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs"><TranslationText>{tUi.rich("totalIncomeFromThisTenantTheSmallerTag0", { tag0: chunks => (<em>{chunks}</em>), tag1: chunks => (<span>{chunks}</span>) })}</TranslationText></TooltipContent>
                    </Tooltip>
                  </span>
                </TableHead>
                <TableHead className={`text-right ${BILLING_COL}`}>
                  <span className="inline-flex items-center gap-1">
                    <SortButton field="cost"><span key={tUi("cost")}>{tUi("cost")}</span></SortButton>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center text-muted-foreground hover:text-foreground"
                          aria-label={tUi("costTooltip")}
                        >
                          <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs"><TranslationText>{tUi.rich("providerCostCOGSForThisTenantSUsage", { tag0: chunks => (<em>{chunks}</em>), tag1: chunks => (<span>{chunks}</span>) })}</TranslationText></TooltipContent>
                    </Tooltip>
                  </span>
                </TableHead>
                <TableHead className={`text-right ${BILLING_COL}`}>
                  <span className="inline-flex items-center gap-1">
                    <SortButton field="profit"><span key={tUi("profit")}>{tUi("profit")}</span></SortButton>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center text-muted-foreground hover:text-foreground"
                          aria-label={tUi("profitTooltip")}
                        >
                          <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        <span key={tUi("revenueCostOperationalOverheadInfraSalariesNotIncluded")}>{tUi("revenueCostOperationalOverheadInfraSalariesNotIncluded")}</span></TooltipContent>
                    </Tooltip>
                  </span>
                </TableHead>
                <TableHead className={`text-right ${BILLING_COL_LAST}`}>
                  <span className="inline-flex items-center gap-1">
                    <SortButton field="margin"><span key={tUi("margin")}>{tUi("margin")}</span></SortButton>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center text-muted-foreground hover:text-foreground"
                          aria-label={tUi("marginTooltip")}
                        >
                          <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        <span key={tUi("profitRevenueDashWhenThereSNoRevenue")}>{tUi("profitRevenueDashWhenThereSNoRevenue")}</span></TooltipContent>
                    </Tooltip>
                  </span>
                </TableHead>
                {canManageTenants && <TableHead className="w-[150px]"><span key={tUi("planChange")}>{tUi("planChange")}</span></TableHead>}
                {hasRowActions && (
                <TableHead className="w-[120px]">
                  <span className="inline-flex items-center gap-1">
                    <span key={tUi("actions")}>{tUi("actions")}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center text-muted-foreground hover:text-foreground"
                          aria-label={tUi("actionsLegend")}
                        >
                          <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        <div className="space-y-1.5">
                          <div className="flex items-start gap-1.5">
                            <Wallet className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <div><TranslationText>{tUi.rich("tag0Tag1WalletTag1Tag0ViewBalanceGrant", { tag0: chunks => (<strong>{chunks}</strong>), tag1: chunks => (<span>{chunks}</span>) })}</TranslationText></div>
                          </div>
                          <div className="flex items-start gap-1.5">
                            <ClipboardList className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <div><TranslationText>{tUi.rich("tag0Tag1AssignChangePlanTag1Tag0Plan", { tag0: chunks => (<strong>{chunks}</strong>), tag1: chunks => (<span>{chunks}</span>), tag2: chunks => (<em>{chunks}</em>), tag3: chunks => (<span>{chunks}</span>), tag4: chunks => (<em>{chunks}</em>), tag5: chunks => (<span>{chunks}</span>), tag6: chunks => (<code>{chunks}</code>) })}</TranslationText></div>
                          </div>
                          <div className="flex items-start gap-1.5">
                            <ArrowUpRight className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <div><TranslationText>{tUi.rich("tag0Tag1EnableSelfServeTag1Tag0Flip", { tag0: chunks => (<strong>{chunks}</strong>), tag1: chunks => (<span>{chunks}</span>), tag2: chunks => (<code>{chunks}</code>), tag3: chunks => (<code>{chunks}</code>) })}</TranslationText></div>
                          </div>
                          <div className="flex items-start gap-1.5">
                            <ArrowLeftRight className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <div><TranslationText>{tUi.rich("tag0Tag1ConvertChannelTag1Tag0MoveTenant", { tag0: chunks => (<strong>{chunks}</strong>), tag1: chunks => (<span>{chunks}</span>), tag2: chunks => (<code>{chunks}</code>) })}</TranslationText></div>
                          </div>
                          <div className="flex items-start gap-1.5">
                            <RotateCcw className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <div><TranslationText>{tUi.rich("tag0Tag1RevertToManualTag1Tag0Cancel", { tag0: chunks => (<strong>{chunks}</strong>), tag1: chunks => (<span>{chunks}</span>), tag2: chunks => (<code>{chunks}</code>) })}</TranslationText></div>
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {tenant.name}
                      {tenant.archived_at && (
                        <Badge variant="outline" className="text-xs text-muted-foreground"><span key={tUi("archived")}>{tUi("archived")}</span></Badge>
                      )}
                      {!tenant.archived_at && !tenant.is_active && (
                        <Badge variant="destructive" className="text-xs"><span key={tUi("inactive")}>{tUi("inactive")}</span></Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {tenant.plan_display_name ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      {tenant.subscription_status ? (
                        <Badge variant="outline" className="text-xs w-fit">{tenant.billing_channel ?? '—'}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground"><span key={tUi("notInitialized")}>{tUi("notInitialized")}</span></span>
                      )}
                      {tenant.payment_provider === 'manual' && (tenant.plan_price_cents ?? 0) > 0 ? (
                        <Badge variant="destructive" className="text-xs w-fit"><span key={tUi("manualPaidPlan")}>{tUi("manualPaidPlan")}</span></Badge>
                      ) : tenant.payment_provider === 'manual' ? (
                        <Badge variant="secondary" className="text-xs w-fit"><span key={tUi("manual")}>{tUi("manual")}</span></Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>{subStatusBadge(tenant.subscription_status, tUi)}</TableCell>
                  <TableCell className="text-right font-mono">
                    <span className={tenant.balance_microusd <= 0 ? 'text-destructive' : ''}>
                      {formatCredits(tenant.balance_microusd)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {tenant.has_active_trial ? (
                      <Badge variant="secondary">
                        {formatCredits(tenant.trial_spent_microusd ?? 0)} <span>/</span> {tenant.trial_budget_microusd != null ? formatCredits(tenant.trial_budget_microusd) : '∞'}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  {/* Period billing: Platform paid / Revenue (with secondary manual) / Cost (with secondary spent) / Profit / Margin %. */}
                  <TableCell className={`text-right font-mono ${BILLING_COL_FIRST}`}>
                    {formatMicroUsd(tenant.platform_paid_cost_microusd)}
                  </TableCell>
                  <TableCell className={`text-right font-mono ${BILLING_COL}`}>
                    <div>{formatMicroUsd(computeRevenueMicrousd(tenant))}</div>
                    <BillingSecondaryLine
                      label={tUi("manual")}
                      microusd={tenant.manual_grant_microusd + tenant.manual_deduct_microusd}
                      signed
                    />
                  </TableCell>
                  <TableCell className={`text-right font-mono ${BILLING_COL}`}>
                    <div>{formatMicroUsd(computeCostMicrousd(tenant))}</div>
                    <BillingSecondaryLine
                      label={tUi("spent")}
                      microusd={tenant.total_charge_microusd}
                    />
                  </TableCell>
                  {(() => {
                    const profit = computeProfitMicrousd(tenant)
                    return (
                      <TableCell className={`text-right font-mono ${BILLING_COL} ${profitColorClass(profit)}`}>
                        {formatMicroUsdSigned(profit)}
                      </TableCell>
                    )
                  })()}
                  {(() => {
                    const margin = computeMarginFraction(tenant)
                    // Margin sign mirrors profit sign — colour by the underlying
                    // microUSD amount so a 0% (zero profit) row stays gray.
                    const colorBasis = margin === null ? 0 : computeProfitMicrousd(tenant)
                    return (
                      <TableCell className={`text-right font-mono ${BILLING_COL_LAST} ${profitColorClass(colorBasis)}`}>
                        {formatMarginPercent(margin)}
                      </TableCell>
                    )
                  })()}
                  {canManageTenants && (
                    <TableCell>
                      <Select
                        value={overrideToValue(tenant.allow_self_serve_plan_change)}
                        onValueChange={(v) => { void updateTenantOverride(tenant, v as SelfServeOverride) }}
                        disabled={overrideSaving === tenant.id}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inherit"><span key={tUi("inheritDefault")}>{tUi("inheritDefault")}</span></SelectItem>
                          <SelectItem value="allow"><span key={tUi("forceAllow")}>{tUi("forceAllow")}</span></SelectItem>
                          <SelectItem value="deny"><span key={tUi("forceDeny")}>{tUi("forceDeny")}</span></SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  )}
                  {hasRowActions && (
                  <TableCell>
                    <div className="flex gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setWalletTenant(tenant)}
                            aria-label={tUi("wallet")}
                          >
                            <Wallet className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent><span key={tUi("walletBalanceGrantCredits")}>{tUi("walletBalanceGrantCredits")}</span></TooltipContent>
                      </Tooltip>
                      {/* Shopify top-up incidents: the settlement sweep can pause a tenant
                          on its own, and this is the only way to lift the hold. */}
                      {/* A hold outlives a channel conversion, so it alone is enough to show
                          the button — otherwise a converted tenant's incident is SQL-only. */}
                      {canViewAll
                        && (tenant.billing_channel === BILLING_CHANNELS.SHOPIFY
                          || tenant.shopify_topups_blocked_at !== null) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setTopupIncidentTenant(tenant)}
                              aria-label={tUi("shopifyTopUps")}
                            >
                              <ShieldAlert className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent><span key={tUi("shopifyTopUpsUnsettledAttemptsHold")}>{tUi("shopifyTopUpsUnsettledAttemptsHold")}</span></TooltipContent>
                        </Tooltip>
                      )}
                      {canManagePlans && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openAssignDialog(tenant)}
                              aria-label={tenant.subscription_status ? tUi("changePlan") : tUi("assignPlan")}
                            >
                              <ClipboardList className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span key={tenant.subscription_status ? tUi("changePlan") : tUi("assignPlan")}>{tenant.subscription_status ? tUi("changePlan") : tUi("assignPlan")}</span>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {canManagePlans && tenant.payment_provider === 'manual' && (
                        // Any manual tenant → self-serve, on either channel: a paid
                        // plan creates a checkout; a free plan is an instant flip
                        // (both Stripe and Shopify support a NULL-subscription free state).
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEnableSelfServe(tenant)}
                              aria-label={tUi("enableSelfServeBilling")}
                            >
                              <ArrowUpRight className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span key={(tenant.plan_price_cents ?? 0) > 0
                              ? tUi("enableSelfServeBillingCreatesCheckoutSession")
                              : tUi("switchToSelfServeFreePlanInstantFlip")}>{(tenant.plan_price_cents ?? 0) > 0
                              ? tUi("enableSelfServeBillingCreatesCheckoutSession")
                              : tUi("switchToSelfServeFreePlanInstantFlip")}</span>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {/* Channel conversion (billing-channel-mobility Phase 3.1). Only
                          shown when a ready target channel exists AND the caller holds
                          `canManageTenants` — mirrors backend PATCH /api/admin/billing/tenants/[id]
                          auth. Paid self-serve rows get a disabled button with tooltip
                          so the admin knows the revert workflow is a pre-requisite.
                          Disabled-button tooltip pattern: wrap in a <span> so the
                          Radix TooltipTrigger still receives pointer events (disabled
                          <button> has pointer-events:none and would silently swallow
                          hover). */}
                      {canManageTenants
                        && tenant.subscription_status
                        && getAvailableTargetChannels(tenant.billing_channel as BillingChannel | null).length > 0
                        && (() => {
                          const isPaidSelfServe =
                            tenant.payment_provider !== 'manual'
                            && (tenant.plan_price_cents ?? 0) > 0
                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={isPaidSelfServe ? 'inline-flex cursor-not-allowed' : 'inline-flex'}>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openConversionDialog(tenant)}
                                    disabled={isPaidSelfServe}
                                    aria-label={tUi("convertBillingChannel")}
                                    className={isPaidSelfServe ? 'pointer-events-none' : ''}
                                  >
                                    <ArrowLeftRight className="h-4 w-4" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <span key={isPaidSelfServe
                                  ? tUi("revertToManualFirstPaidSelfServeTenants")
                                  : tUi("convertBillingChannelShopifyStripeBothDirectionsConverting")}>{isPaidSelfServe
                                  ? tUi("revertToManualFirstPaidSelfServeTenants")
                                  : tUi("convertBillingChannelShopifyStripeBothDirectionsConverting")}</span>
                              </TooltipContent>
                            </Tooltip>
                          )
                        })()}
                      {/* Revert to manual (billing-channel-mobility Phase 3.3). Visible
                          for self-serve paid rows where there's something to revert. */}
                      {canManageTenants && tenant.payment_provider && tenant.payment_provider !== 'manual' && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openRevertDialog(tenant)}
                              aria-label={tUi("revertToManualBilling")}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <span key={tUi("revertToPlatformManagedManualBillingCancelsThe")}>{tUi("revertToPlatformManagedManualBillingCancelsThe")}</span></TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  )}
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11 + (canManageTenants ? 1 : 0) + (hasRowActions ? 1 : 0)} className="text-center text-muted-foreground py-8">
                    <span key={tenants.length === 0 ? tUi("noTenantsFound") : tUi("noTenantsMatchFilters")}>{tenants.length === 0 ? tUi("noTenantsFound") : tUi("noTenantsMatchFilters")}</span>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      {walletTenant && (
        <TenantWalletDialog
          open={!!walletTenant}
          onOpenChange={(open) => { if (!open) setWalletTenant(null) }}
          tenantId={walletTenant.id}
          tenantName={walletTenant.name}
          canGrant={canGrantCredits}
          onChanged={fetchTenants}
        />
      )}

      {topupIncidentTenant && (
        <ShopifyTopupIncidentDialog
          open={!!topupIncidentTenant}
          onOpenChange={(open) => { if (!open) setTopupIncidentTenant(null) }}
          tenantId={topupIncidentTenant.id}
          tenantName={topupIncidentTenant.name}
          canResolve={canGrantCredits}
          canClearBlock={canManageTenants}
          onChanged={fetchTenants}
        />
      )}

      {/* Assign Plan Dialog */}
      <Dialog open={!!assignTenant} onOpenChange={(open) => { if (!open) setAssignTenant(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><span key={assignTenant?.subscription_status ? tUi("changePlan2") : tUi("initializeBilling")}>{assignTenant?.subscription_status ? tUi("changePlan2") : tUi("initializeBilling")}</span></DialogTitle>
            <DialogDescription>
              {assignTenant?.subscription_status
                ? <><TranslationText>{tUi.rich("changeBillingPlanForTag0Value0Tag0", { value0: String(assignTenant?.name), tag0: chunks => (<strong>{chunks}</strong>) })}</TranslationText></>
                : <><TranslationText>{tUi.rich("initializeBillingForTag0Value0Tag0ChooseBilling", { value0: String(assignTenant?.name), tag0: chunks => (<strong>{chunks}</strong>) })}</TranslationText></>
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Channel selector — only for first assignment */}
            {!assignTenant?.subscription_status && (
              <div>
                <p className="text-sm font-medium mb-1.5"><span key={tUi("billingChannel")}>{tUi("billingChannel")}</span></p>
                <Select value={assignChannel} onValueChange={(v) => { setAssignChannel(v); setAssignPlanId('') }}>
                  <SelectTrigger>
                    <SelectValue placeholder={tUi("selectChannel")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe"><span key={"Stripe"}>{"Stripe"}</span></SelectItem>
                    <SelectItem value="shopify"><span key={"Shopify"}>{"Shopify"}</span></SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1"><span key={tUi("cannotBeChangedAfterFirstPlanAssignment")}>{tUi("cannotBeChangedAfterFirstPlanAssignment")}</span></p>
              </div>
            )}

            {/* Sync-with-Stripe checkbox — admin-plan-change-proration Phase 2.1.
                Only shown for Stripe rows on a plan change (not first assignment).
                Adaptive description below explains what each state does. */}
            {assignTenant?.subscription_status && showSyncWithStripeCheckbox(assignTenant) && (
              <div className="space-y-1.5 rounded-md border bg-muted/40 px-3 py-2.5">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={assignSyncWithStripe}
                    onCheckedChange={(v) => setAssignSyncWithStripe(v === true)}
                    className="mt-0.5"
                    aria-label={tUi("syncWithStripe")}
                  />
                  <span className="font-medium"><span key={tUi("syncWithStripe")}>{tUi("syncWithStripe")}</span></span>
                </label>
                <p className="text-xs text-muted-foreground pl-6">
                  {changePlanDialogDescription({
                    syncChecked: assignSyncWithStripe,
                    hasLiveSub: assignTenant.stripe_subscription_id !== null,
                    subscriptionStatus: assignTenant.subscription_status,
                  })}
                </p>
              </div>
            )}

            {/* Plan selector */}
            {plansLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> <span><span key={tUi("loadingPlans")}>{tUi("loadingPlans")}</span></span>
              </div>
            ) : plansError ? (
              <div className="flex items-center gap-2">
                <p className="text-sm text-destructive">{plansError}</p>
                <Button variant="outline" size="sm" onClick={loadPlans}><span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button>
              </div>
            ) : !assignChannel ? (
              <p className="text-sm text-muted-foreground"><span key={tUi("selectABillingChannelFirst")}>{tUi("selectABillingChannelFirst")}</span></p>
            ) : (() => {
              const channelPlans = plans.filter((p) => p.billing_channel === assignChannel && (p.status === 'published' || p.status === 'internal'))
              return channelPlans.length === 0 ? (
                <p className="text-sm text-muted-foreground"><span><span key={tUi("noPlansAvailableFor")}>{tUi("noPlansAvailableFor")}</span></span> {assignChannel} <span><span key={tUi("channel")}>{tUi("channel")}</span></span></p>
              ) : (
                <Select value={assignPlanId} onValueChange={setAssignPlanId}>
                  <SelectTrigger>
                    <SelectValue placeholder={tUi("selectAPlan")} />
                  </SelectTrigger>
                  <SelectContent>
                    {channelPlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.display_name} <span className="text-muted-foreground">({plan.name})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTenant(null)} disabled={assignLoading}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
            <Button onClick={handleAssignPlan} disabled={assignLoading || !assignPlanId}>
              {assignLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <span key={assignTenant?.subscription_status ? tUi("changePlan2") : tUi("assignPlan2")}>{assignTenant?.subscription_status ? tUi("changePlan2") : tUi("assignPlan2")}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Channel conversion dialog (billing-channel-mobility Phase 3.2) */}
      <Dialog
        open={!!conversionTenant}
        onOpenChange={(open) => { if (!open && !conversionLoading) setConversionTenant(null) }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle><span key={tUi("convertBillingChannel2")}>{tUi("convertBillingChannel2")}</span></DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2">
                <div>
                  <span><span key={tUi("convert")}>{tUi("convert")}</span></span> <strong>{conversionTenant?.name}</strong> <span><span key={tUi("from")}>{tUi("from")}</span></span>
                  {' '}<Badge variant="outline" className="ml-0.5 mr-0.5">{conversionTenant?.billing_channel ?? '—'}</Badge>
                  {' '}<span><span key={tUi("toANewBillingChannelExternalSubscriptionIDs")}>{tUi("toANewBillingChannelExternalSubscriptionIDs")}</span></span>
                </div>
                {(() => {
                  const target = plans.find((p) => p.id === conversionPlanId)
                  if (!target) return null
                  const isPaidTarget = target.price_monthly_cents > 0
                  return isPaidTarget ? (
                    <div className="text-xs rounded-md border bg-muted/40 px-2 py-1.5">
                      <strong><span key={tUi("paidTarget")}>{tUi("paidTarget")}</span></strong> <span><span key={tUi("tenantStaysOn")}>{tUi("tenantStaysOn")}</span></span> <em><span key={tUi("manual")}>{tUi("manual")}</span></em> <span><span key={tUi("billingWithAFreshMonthlyPeriodUse")}>{tUi("billingWithAFreshMonthlyPeriodUse")}</span></span> <em><span key={tUi("enableSelfServe")}>{tUi("enableSelfServe")}</span></em>{' '}
                      <span><span key={tUi("afterwardsToCreateTheCheckoutOnTheNew")}>{tUi("afterwardsToCreateTheCheckoutOnTheNew")}</span></span>
                    </div>
                  ) : (
                    <div className="text-xs rounded-md border bg-muted/40 px-2 py-1.5">
                      <strong><span key={tUi("freeTarget")}>{tUi("freeTarget")}</span></strong> <span><span key={tUi("tenantFlipsDirectlyToSelfServeOnThe")}>{tUi("tenantFlipsDirectlyToSelfServeOnThe")}</span></span>
                    </div>
                  )
                })()}
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Target channel selector — dropdown shape ready for N>1 providers. */}
            <div>
              <p className="text-sm font-medium mb-1.5"><span key={tUi("targetChannel")}>{tUi("targetChannel")}</span></p>
              {(() => {
                const targets = getAvailableTargetChannels(
                  (conversionTenant?.billing_channel ?? null) as BillingChannel | null,
                )
                if (targets.length === 0) {
                  return <p className="text-sm text-muted-foreground"><span key={tUi("noReadyTargetChannel")}>{tUi("noReadyTargetChannel")}</span></p>
                }
                return (
                  <Select
                    value={conversionChannel}
                    onValueChange={(v) => { setConversionChannel(v as BillingChannel); setConversionPlanId('') }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={tUi("selectTargetChannel")} />
                    </SelectTrigger>
                    <SelectContent>
                      {targets.map((ch) => (
                        <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              })()}
            </div>

            {/* Target plan selector — required; filtered by target channel + status internal/published. */}
            <div>
              <p className="text-sm font-medium mb-1.5"><span key={tUi("targetPlan")}>{tUi("targetPlan")}</span></p>
              {plansLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> <span><span key={tUi("loadingPlans")}>{tUi("loadingPlans")}</span></span>
                </div>
              ) : plansError ? (
                <div className="flex items-center gap-2">
                  <p className="text-sm text-destructive">{plansError}</p>
                  <Button variant="outline" size="sm" onClick={loadPlans}><span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button>
                </div>
              ) : !conversionChannel ? (
                <p className="text-sm text-muted-foreground"><span key={tUi("selectTargetChannelFirst")}>{tUi("selectTargetChannelFirst")}</span></p>
              ) : (() => {
                // Plan names are channel-specific (`free-stripe` vs `free-shopify`),
                // so auto-match is impossible — admin picks explicitly. We include
                // both `internal` and `published` statuses: the server accepts both,
                // and legacy admin-only plans are often `internal`.
                const targetPlans = plans.filter(
                  (p) => p.billing_channel === conversionChannel && (p.status === 'internal' || p.status === 'published'),
                )
                if (targetPlans.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground">
                      <span><span key={tUi("noAssignablePlansOnThe")}>{tUi("noAssignablePlansOnThe")}</span></span> <strong>{conversionChannel}</strong> <span><span key={tUi("channelCreateAStripeEquivalentPlanInThe")}>{tUi("channelCreateAStripeEquivalentPlanInThe")}</span></span>
                    </p>
                  )
                }
                return (
                  <Select value={conversionPlanId} onValueChange={setConversionPlanId}>
                    <SelectTrigger>
                      <SelectValue placeholder={tUi("selectATargetPlan")} />
                    </SelectTrigger>
                    <SelectContent>
                      {targetPlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.display_name} <span className="text-muted-foreground">({plan.name}<span key={plan.status === 'internal' ? tUi("internal") : ''}>{plan.status === 'internal' ? tUi("internal") : ''}</span>)</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConversionTenant(null)}
              disabled={conversionLoading}
            ><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
            <Button
              onClick={handleConvertChannel}
              disabled={conversionLoading || !conversionChannel || !conversionPlanId}
            >
              {conversionLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <span><span key={tUi("convertChannel2")}>{tUi("convertChannel2")}</span></span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert-to-manual confirmation (billing-channel-mobility Phase 3.3) */}
      <AlertDialog
        open={!!revertTenant}
        onOpenChange={(open) => { if (!open && !revertLoading) setRevertTenant(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span key={tUi("revertToPlatformManagedBilling")}>{tUi("revertToPlatformManagedBilling")}</span></AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>
                  <span><span key={tUi("cancelTheExternalSubscriptionFor")}>{tUi("cancelTheExternalSubscriptionFor")}</span></span> <strong>{revertTenant?.name}</strong> <span><span key={tUi("andFlipTo")}>{tUi("andFlipTo")}</span></span> <strong><span key={tUi("manual")}>{tUi("manual")}</span></strong> <span><span key={tUi("billingImmediately")}>{tUi("billingImmediately")}</span></span>
                </div>
                <ul className="list-disc pl-5 text-sm">
                  <li><span key={tUi("externalSubscriptionIsCanceledImmediatelyNotAtPeriod")}>{tUi("externalSubscriptionIsCanceledImmediatelyNotAtPeriod")}</span></li>
                  <li><span key={tUi("tenantStaysOnTheSamePlanAndBilling")}>{tUi("tenantStaysOnTheSamePlanAndBilling")}</span></li>
                  <li><span key={tUi("billingPeriodResetsToAFreshMonthlyCycle")}>{tUi("billingPeriodResetsToAFreshMonthlyCycle")}</span></li>
                  <li><span key={tUi("walletBalanceIsPreserved")}>{tUi("walletBalanceIsPreserved")}</span></li>
                </ul>
                <div className="text-xs text-muted-foreground">
                  <span><span key={tUi("currentProvider")}>{tUi("currentProvider")}</span></span> <strong>{revertTenant?.payment_provider}</strong>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revertLoading}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handleRevertToManual() }}
              disabled={revertLoading}
            >
              {revertLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <span><span key={tUi("revertToManual2")}>{tUi("revertToManual2")}</span></span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  )
}
